import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../api/auth.js';
import { post } from '../api/client.js';
import { Card, Field, Notice } from '../components/ui.js';

/**
 * First-run setup (§6, §7 page 1): consumes the one-time token printed by the
 * installer and creates the first directorate and its administrator. There is no
 * default password: the operator chooses one here, once.
 */
export function SetupPage(): JSX.Element {
  const [params] = useSearchParams();
  const { login } = useAuth();

  const [form, setForm] = useState({
    setup_token: params.get('token') ?? '',
    email: '',
    display_name: '',
    password: '',
    confirm: '',
    tenant_name: '',
    tenant_slug: '',
    timezone: 'Asia/Baghdad',
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (form.password !== form.confirm) {
      setError('كلمتا المرور غير متطابقتين');
      return;
    }
    if (form.password.length < 12) {
      setError('كلمة المرور يجب ألا تقل عن 12 محرفًا');
      return;
    }
    setBusy(true);
    try {
      await post('/auth/setup', {
        setup_token: form.setup_token.trim(),
        email: form.email.trim(),
        display_name: form.display_name.trim(),
        password: form.password,
        tenant_name: form.tenant_name.trim(),
        tenant_slug: form.tenant_slug.trim().toLowerCase(),
        timezone: form.timezone.trim(),
      });
      await login(form.email.trim(), form.password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'تعذر إكمال الإعداد');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-shell">
      <div className="login-card" style={{ width: 'min(560px, 100%)' }}>
        <h1 style={{ textAlign: 'center', color: 'var(--brand)' }}>الإعداد الأولي</h1>
        <Notice tone="info">
          هذه الخطوة تُنفَّذ مرة واحدة. رمز الإعداد يُطبع عند تثبيت الخادم، وينتهي أثره بعد الاستخدام.
        </Notice>

        <Card>
          <form onSubmit={submit}>
            <Field label="رمز الإعداد" hint="من مخرجات سكربت التثبيت">
              <input required value={form.setup_token} onChange={set('setup_token')} className="mono" />
            </Field>

            <div className="grid cols-2">
              <Field label="اسم المديرية">
                <input required value={form.tenant_name} onChange={set('tenant_name')} />
              </Field>
              <Field label="المعرّف المختصر" hint="حروف لاتينية صغيرة وأرقام وشرطات">
                <input
                  required
                  pattern="[a-z0-9]([a-z0-9-]*[a-z0-9])?"
                  value={form.tenant_slug}
                  onChange={set('tenant_slug')}
                  className="mono"
                />
              </Field>
            </div>

            <Field label="المنطقة الزمنية" hint="اسم IANA، مثل Asia/Baghdad">
              <input required value={form.timezone} onChange={set('timezone')} className="mono" />
            </Field>

            <div className="grid cols-2">
              <Field label="اسم المسؤول">
                <input required value={form.display_name} onChange={set('display_name')} />
              </Field>
              <Field label="البريد الإلكتروني">
                <input type="email" required value={form.email} onChange={set('email')} />
              </Field>
            </div>

            <div className="grid cols-2">
              <Field label="كلمة المرور" hint="12 محرفًا على الأقل">
                <input
                  type="password"
                  required
                  minLength={12}
                  autoComplete="new-password"
                  value={form.password}
                  onChange={set('password')}
                />
              </Field>
              <Field label="تأكيد كلمة المرور">
                <input
                  type="password"
                  required
                  minLength={12}
                  autoComplete="new-password"
                  value={form.confirm}
                  onChange={set('confirm')}
                />
              </Field>
            </div>

            {error ? (
              <div className="notice danger" role="alert">
                {error}
              </div>
            ) : null}

            <button className="primary" type="submit" disabled={busy} style={{ width: '100%' }}>
              {busy ? 'جارٍ الإنشاء…' : 'إنشاء المديرية والمسؤول'}
            </button>
          </form>
        </Card>
      </div>
    </div>
  );
}
